import { computed } from "vue";
import type { ComputedRef, CreateElement, VNode } from "vue";
import BmText from "@/components/controls/input/BmText.vue";
import BmTextArea from "@/components/controls/input/BmTextArea.vue";
import BmNumberQ from "@/components/controls/input/BmNumberQ.vue";
import BmWeight from "@/components/controls/input/specialised/BmWeight.vue";
import BmVolumeGeneral from "@/components/controls/input/specialised/BmVolumeGeneral.vue";
import BmDateQ from "@/components/controls/input/BmDateQ.vue";
import BmTime from "@/components/controls/input/BmTime.vue";
import BmDateTime from "@/components/controls/input/BmDateTime.vue";
import BmPassword from "@/components/controls/input/BmPassword.vue";
import BmAddress from "@/components/controls/address/BmAddress.vue";
import BmSwitch from "@/components/controls/input/BmSwitch.vue";
import BmTelephone from "@/components/controls/input/specialised/BmTelephone.vue";
import BmEmail from "@/components/controls/input/specialised/BmEmail.vue";
import { makeRef } from "@/components/composables/reactivityTypes";
import type Key from "@/app/types/common/key";
import type { ValidationRule } from "@/plugins/validation";
import { ruleSet } from "@/plugins/validation";
import type { LocalTime } from "@js-joda/core";
import { Instant, LocalDate } from "@js-joda/core";
import {
  cloneAddress,
  type Address,
} from "@/components/controls/address/addresses";
import type { PartialQ } from "@/controls/tpcontrol";
import {
  controlDefaults,
  extendDefaults,
  TpControl,
} from "@/controls/tpcontrol";
import BmCheckbox from "@/components/controls/input/BmCheckbox.vue";
import type { Location } from "vue-router";

const inputDefaults = extendDefaults(controlDefaults, {
  translateKey: null as Key | null,
  label: "",
  required: false,
  loading: false,
  suffix: "",
  routerLocation: undefined as Location | undefined,
});

type InputIn = typeof inputDefaults;

export abstract class InputBase<
  TIn extends InputIn = InputIn,
> extends TpControl<TIn> {
  public onInput: () => void = () => {};

  constructor(defaults: TIn, ...opts: PartialQ<TIn>[]) {
    super(defaults, ...opts);
  }
}

const textBoxDefaults = extendDefaults(inputDefaults, {
  placeholder: null as string | null,
  readOnlyValue: null as string | null,
});

type TextBoxIn = typeof textBoxDefaults;

export class TextBox extends InputBase<TextBoxIn> {
  protected _value = makeRef("");

  get val() {
    return this._value.value;
  }

  setValue(val: string) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<TextBoxIn>[]) {
    super(textBoxDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: any) => {
      this._value.value = ev ?? "";
      this.onInput();
    };
    return h(BmText, {
      props: {
        value: this._value.value,
        placeholder: this.in.placeholder,
        suffix: this.in.suffix || undefined,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        rules: this.in.required ? [ruleSet.required()] : [],
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        readOnlyValue: this.in.readOnlyValue,
        routerLocation: this.in.routerLocation,
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

const textAreaDefaults = extendDefaults(inputDefaults, {
  placeholder: null as string | null,
});

type TextAreaIn = typeof textAreaDefaults;

export class TextArea extends InputBase<TextAreaIn> {
  protected _value = makeRef("");

  get val() {
    return this._value.value;
  }

  setValue(val: string) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<TextAreaIn>[]) {
    super(textAreaDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: any) => {
      this._value.value = ev ?? "";
      this.onInput();
    };
    return h(BmTextArea, {
      props: {
        value: this._value.value,
        placeholder: this.in.placeholder,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        rules: this.in.required ? [ruleSet.required()] : [],
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        suffix: this.in.suffix || undefined,
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class PasswordBox extends InputBase<TextBoxIn> {
  protected _value = makeRef("");

  get val() {
    return this._value.value;
  }

  setValue(val: string) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<TextBoxIn>[]) {
    super(textBoxDefaults, ...opts, { readOnlyValue: "********" });
  }

  render(h: CreateElement): VNode {
    const inp = (ev: any) => {
      this._value.value = ev ?? "";
      this.onInput();
    };
    return h(BmPassword, {
      props: {
        value: this._value.value,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        rules: this.in.required ? [ruleSet.required()] : [],
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        placeholder: this.in.placeholder,
        readOnlyValue: this.in.readOnlyValue,
        suffix: this.in.suffix || undefined,
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

const numberBoxDefaults = extendDefaults(inputDefaults, {
  integral: false,
  min: null as number | null,
  max: null as number | null,
  above: null as number | null,
  below: null as number | null,
  placeholder: null as number | null,
});
type NumberBoxIn = typeof numberBoxDefaults;

export class NumberQBox extends InputBase<NumberBoxIn> {
  protected _value = makeRef<number | null>(null);
  private _rules: ComputedRef<readonly ValidationRule[]>;

  get val() {
    return this._value.value;
  }

  setValue(val: number | null) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<NumberBoxIn>[]) {
    super(numberBoxDefaults, ...opts);
    this._rules = computed(() => this.resolveRules());
  }

  get isValid() {
    return this._rules.value
      .map((x) => x(this._value.value))
      .every((x) => x === true || x === "");
  }

  private resolveLimits() {
    // Although unlikely, lets unravel cases where above and min  or below and max are both specified
    let min = this.in.min;
    let max = this.in.max;
    let above = this.in.above;
    let below = this.in.below;

    if (min !== null && above !== null) {
      if (min > above) {
        // Min is more restrictive than above, so just use min
        above = null;
      } else {
        // Above is more restrictive than min, so just use above
        min = null;
      }
    }

    if (max !== null && below !== null) {
      if (max < below) {
        // Max is more restrictive than below, so just use mx
        below = null;
      } else {
        // Below is more restrictive than max, so just use below
        max = null;
      }
    }

    return {
      min,
      max,
      above,
      below,
    };
  }

  private resolveRules() {
    const rules: ValidationRule[] = this.in.required
      ? [ruleSet.required()]
      : [];

    const lim = this.resolveLimits();

    if (lim.min !== null) {
      if (lim.min === 0) {
        // Apply special not negative rule instead of general range.
        rules.push(ruleSet.notNegativeNumber());
        lim.min = null;
      } else if (lim.min > 0) {
        // Apply special not negative rule as well as general range.
        rules.push(ruleSet.notNegativeNumber());
      }
    } else if (lim.above !== null) {
      if (lim.above === 0) {
        // Apply special not negative and not zero rules instead of general range.
        rules.push(ruleSet.notNegativeNumber());
        rules.push(ruleSet.notZero());
        lim.above = null;
      } else if (lim.above > 0) {
        // Apply special not negative rule as well as general range.
        rules.push(ruleSet.notNegativeNumber());
      }
    }

    if (
      lim.min !== null ||
      lim.max !== null ||
      lim.above !== null ||
      lim.below !== null
    ) {
      // Apply general range rule
      // At most one of min/above, and one of max/below, will be non-null at this point.
      rules.push(
        ruleSet.numberInRange(
          lim.min ?? lim.above ?? undefined,
          lim.max ?? lim.below ?? undefined,
          lim.min !== null,
          lim.max !== null
        )
      );
    }

    return rules;
  }

  render(h: CreateElement): VNode {
    const inp = (ev: number | null) => {
      this._value.value = ev;
      this.onInput();
    };

    const lim = this.resolveLimits();

    return h(BmNumberQ, {
      props: {
        value: this._value.value,
        integral: this.in.integral,
        defaultValue: null,
        // BmNumberQ min/max props affect its stepper not validation.
        min: lim.min ?? lim.above ?? undefined,
        max: lim.max ?? lim.below ?? undefined,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        rules: this._rules.value,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        suffix: this.in.suffix || undefined,
        placeholder: this.in.placeholder?.toString(),
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class NumberBox extends NumberQBox {
  constructor(...opts: PartialQ<NumberBoxIn>[]) {
    super(...opts, { required: true });
  }

  get val() {
    return super.val ?? 0;
  }

  setValue(val: number) {
    super.setValue(val);
  }
}

const dateBoxDefaults = extendDefaults(inputDefaults, {
  evaluateHighlight: null as ((date: LocalDate) => string) | null,
  errors: [] as readonly string[],
});

type DateBoxIn = typeof dateBoxDefaults;

export class DateQBox extends InputBase<DateBoxIn> {
  protected _value = makeRef<LocalDate | null>(null);

  get val() {
    return this._value.value;
  }

  setValue(val: LocalDate | null) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<DateBoxIn>[]) {
    super(dateBoxDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: LocalDate | null) => {
      this._value.value = ev;
      this.onInput();
    };

    const rules: ValidationRule[] = this.in.required
      ? [ruleSet.required()]
      : [];

    return h(BmDateQ, {
      props: {
        value: this._value.value,
        clearable: !this.in.required,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        rules,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        suffix: this.in.suffix || undefined,
        errorMessages: this.in.errors,
        dateEvents: this.in.evaluateHighlight || undefined,
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class DateBox extends DateQBox {
  constructor(...opts: PartialQ<DateBoxIn>[]) {
    super(...opts, { required: true });
  }

  get val() {
    return super.val ?? LocalDate.EPOCH_0;
  }

  setValue(val: LocalDate) {
    super.setValue(val.isEqual(LocalDate.EPOCH_0) ? null : val);
  }

  setValueOrClear(val: LocalDate | null) {
    super.setValue(val);
  }

  get isValid() {
    return !!super.val;
  }
}

export class TimeQBox extends InputBase {
  protected _value = makeRef<LocalTime | null>(null);

  get val() {
    return this._value.value;
  }

  setValue(val: LocalTime | null) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<InputIn>[]) {
    super(inputDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: LocalTime | null) => {
      this._value.value = ev;
      this.onInput();
    };

    const rules: ValidationRule[] = this.in.required
      ? [ruleSet.required()]
      : [];

    return h(BmTime, {
      props: {
        value: this._value.value,
        clearable: !this.in.required,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        rules,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        suffix: this.in.suffix || undefined,
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class DateTimeQBox extends InputBase {
  protected _value = makeRef<Instant | null>(null);

  get val() {
    return this._value.value;
  }

  setValue(val: Instant | null) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<InputIn>[]) {
    super(inputDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: Instant | null) => {
      this._value.value = ev;
      this.onInput();
    };

    const rules: ValidationRule[] = this.in.required
      ? [ruleSet.required()]
      : [];

    return h(BmDateTime, {
      props: {
        value: this._value.value,
        clearable: !this.in.required,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        rules,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        suffix: this.in.suffix || undefined,
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class DateTimeBox extends DateTimeQBox {
  constructor(...opts: PartialQ<InputIn>[]) {
    super(...opts, { required: true });
    this.setValue(Instant.EPOCH);
  }

  get val() {
    return super.val ?? Instant.EPOCH;
  }

  setValue(val: Instant) {
    super.setValue(val);
  }
}

export class AddressBox extends InputBase {
  protected _value = makeRef<Address>({
    addressBusinessName: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    addressLine4: "",
    addressLine5: "",
    addressPostcode: "",
    addressLine6: "",
  });

  get val() {
    return this._value.value;
  }

  setValue(val: Address) {
    this._value.value = cloneAddress(val);
  }

  constructor(...opts: PartialQ<InputIn>[]) {
    super(inputDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (update: (addr: Address) => void) => {
      const clone = cloneAddress(this._value.value);
      update(clone);
      this._value.value = clone;
      this.onInput();
    };

    return h(BmAddress, {
      props: {
        addressBusinessName: this._value.value.addressBusinessName,
        addressLine1: this._value.value.addressLine1,
        addressLine2: this._value.value.addressLine2,
        addressLine3: this._value.value.addressLine3,
        addressLine4: this._value.value.addressLine4,
        addressLine5: this._value.value.addressLine5,
        addressPostcode: this._value.value.addressPostcode,
        addressLine6: this._value.value.addressLine6,
        suffix: this.in.suffix || undefined,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
      },
      on: {
        "update:address-business-name": (value: string) =>
          inp((x) => (x.addressBusinessName = value ?? "")),
        "update:address-line1": (value: string) =>
          inp((x) => (x.addressLine1 = value ?? "")),
        "update:address-line2": (value: string) =>
          inp((x) => (x.addressLine2 = value ?? "")),
        "update:address-line3": (value: string) =>
          inp((x) => (x.addressLine3 = value ?? "")),
        "update:address-line4": (value: string) =>
          inp((x) => (x.addressLine4 = value ?? "")),
        "update:address-line5": (value: string) =>
          inp((x) => (x.addressLine5 = value ?? "")),
        "update:address-postcode": (value: string) =>
          inp((x) => (x.addressPostcode = value ?? "")),
        "update:address-line6": (value: string) =>
          inp((x) => (x.addressLine6 = value ?? "")),
      },
    });
  }
}

const booleanDefaults = extendDefaults(inputDefaults, {
  showAsCb: false,
});

type BooleanIn = typeof booleanDefaults;

export class BooleanSwitch extends InputBase<BooleanIn> {
  protected _value = makeRef(false);

  get val() {
    return this._value.value;
  }

  setValue(val: boolean) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<BooleanIn>[]) {
    super(booleanDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: any) => {
      this._value.value = ev ?? false;
      this.onInput();
    };
    return h(this.in.showAsCb ? BmCheckbox : BmSwitch, {
      props: {
        value: this._value.value,
        suffix: this.in.suffix || undefined,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class TelephoneBox extends InputBase {
  protected _value = makeRef("");

  get val() {
    return this._value.value;
  }

  setValue(val: string) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<InputIn>[]) {
    super(inputDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: any) => {
      this._value.value = ev ?? "";
      this.onInput();
    };

    return h(BmTelephone, {
      props: {
        value: this._value.value,
        suffix: this.in.suffix || undefined,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        rules: this.in.required ? [ruleSet.required()] : [],
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class EmailBox extends InputBase {
  protected _value = makeRef("");

  get val() {
    return this._value.value;
  }

  setValue(val: string) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<InputIn>[]) {
    super(inputDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: any) => {
      this._value.value = ev ?? "";
      this.onInput();
    };

    const rules = [ruleSet.emails()];
    if (this.in.required) {
      rules.push(ruleSet.required());
    }
    return h(BmEmail, {
      props: {
        value: this._value.value,
        suffix: this.in.suffix || undefined,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        rules,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class WeightQBox extends InputBase {
  protected _value = makeRef<number | null>(null);

  get val() {
    return this._value.value;
  }

  setValue(val: number | null) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<InputIn>[]) {
    super(inputDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: number | null) => {
      this._value.value = ev;
      this.onInput();
    };

    return h(BmWeight, {
      props: {
        value: this._value.value,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        suffix: this.in.suffix || undefined,
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class WeightBox extends WeightQBox {
  constructor(...opts: PartialQ<InputIn>[]) {
    super(...opts, { required: true });
  }

  get val() {
    return super.val ?? 0;
  }

  setValue(val: number) {
    super.setValue(val);
  }
}

export class VolumeGeneralQBox extends InputBase {
  protected _value = makeRef<number | null>(null);

  get val() {
    return this._value.value;
  }

  setValue(val: number | null) {
    this._value.value = val;
  }

  constructor(...opts: PartialQ<InputIn>[]) {
    super(inputDefaults, ...opts);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: number | null) => {
      this._value.value = ev;
      this.onInput();
    };

    return h(BmVolumeGeneral, {
      props: {
        value: this._value.value,
      },
      attrs: {
        t: this.in.translateKey,
        label: this.in.label,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        suffix: this.in.suffix || undefined,
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class VolumeGeneralBox extends VolumeGeneralQBox {
  constructor(...opts: PartialQ<InputIn>[]) {
    super(...opts, { required: true });
  }

  get val() {
    return super.val ?? 0;
  }

  setValue(val: number) {
    super.setValue(val);
  }
}

const inputs = {
  address: (opts: Partial<InputIn>) => new AddressBox(opts),
  boolean: (opts: Partial<BooleanIn>) => new BooleanSwitch(opts),
  dateQ: (opts: Partial<DateBoxIn>) => new DateQBox(opts),
  date: (opts: Partial<DateBoxIn>) => new DateBox(opts),
  dateTimeQ: (opts: Partial<InputIn>) => new DateTimeQBox(opts),
  dateTime: (opts: Partial<InputIn>) => new DateTimeBox(opts),
  displayText: (opts: Partial<Omit<TextBoxIn, "readOnly">>) =>
    new TextBox(opts, { readOnly: true }),
  email: (opts?: Partial<InputIn>) => new EmailBox(opts),
  numberQ: (opts?: Partial<NumberBoxIn>) => new NumberQBox(opts),
  number: (opts?: Partial<NumberBoxIn>) => new NumberBox(opts),
  password: () => new PasswordBox(),
  telephone: () => new TelephoneBox(),
  text: (opts: Partial<TextBoxIn>) => new TextBox(opts),
  textArea: (opts: Partial<TextAreaIn>) => new TextArea(opts),
  timeQ: (opts: Partial<InputIn>) => new TimeQBox(opts),
  volumeGeneral: (opts: Partial<InputIn>) => new VolumeGeneralBox(opts),
  weight: (opts: Partial<InputIn>) => new WeightBox(opts),
};

export { inputs };
